import type { PeoplePerson, PeopleSource } from "@playbookai/types";
import {
  peopleFieldChipView,
  type PeopleFieldChipView,
} from "./people-field-chip.js";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import {
  buildPeopleFieldModel,
  type PeopleFieldModel,
} from "./people-field-model.js";
import type {
  PeopleFieldGrantTiming,
  PeopleFieldMode,
  PeopleFieldValue,
} from "./people-field.types.js";
import {
  usePeopleFieldSearch,
  type PeopleFieldSearch,
} from "./usePeopleFieldSearch.js";
import { useResolvedPerson } from "./useResolvedPerson.js";

export interface PeopleFieldData {
  readonly roster: readonly PeoplePerson[];
  /** The open list's sections, rows and search state. */
  readonly model: PeopleFieldModel;
  /** The value as the chip shows it; `null` when nobody is assigned. */
  readonly view: PeopleFieldChipView | null;
  readonly search: PeopleFieldSearch;
}

/**
 * Everything PeopleField shows, read from its `source`: the roster (from
 * memory), what `resolve` says about a value or a current user the roster
 * does not hold (asked lazily, d34), the workspace search, the open list's
 * model and the chip. `present` is the list's mounted lifetime, so what it
 * shows stays put while it fades out.
 */
export function usePeopleFieldData({
  source,
  value,
  query,
  present,
  mode,
  allowTyped,
  grantTiming,
  pendingGrant,
  labels,
}: {
  readonly source: PeopleSource;
  readonly value: PeopleFieldValue;
  readonly query: string;
  readonly present: boolean;
  readonly mode: PeopleFieldMode;
  readonly allowTyped: boolean;
  readonly grantTiming: PeopleFieldGrantTiming;
  readonly pendingGrant: boolean;
  readonly labels: PeopleFieldResolvedLabels;
}): PeopleFieldData {
  const roster = source.roster();
  const personId = value?.kind === "person" ? value.id : null;
  const rosterPerson = personId
    ? roster.find((person) => person.id === personId)
    : undefined;
  const resolved = useResolvedPerson(
    source,
    personId && !rosterPerson ? personId : null,
  );
  const meId = source.currentUserId;
  const rosterMe = meId
    ? roster.find((person) => person.id === meId)
    : undefined;
  const meResolved = useResolvedPerson(
    source,
    present && meId && !rosterMe && mode !== "share" ? meId : null,
  );
  const me: PeoplePerson | null =
    rosterMe ??
    (meResolved.status === "done" && meResolved.person
      ? { id: meResolved.person.id, name: meResolved.person.name }
      : null);

  const search = usePeopleFieldSearch({ source, query, enabled: present });
  const model = buildPeopleFieldModel({
    mode,
    grantMode: source.grantMode,
    roster,
    currentUserId: meId,
    me,
    value,
    query,
    search,
    allowTyped,
    grantTiming,
    labels,
  });
  const view = value
    ? peopleFieldChipView({
        value,
        rosterPerson,
        grantMode: source.grantMode,
        pendingGrant,
        resolved,
        labels,
      })
    : null;
  return { roster, model, view, search };
}
